import React from "react";
import {
  getGlobalConfig,
  getAgreementsConfig,
  getAboutUsConfig,
  transformStaticProps,
  getAgreementsPaths,
  getAboutUsPaths,
  getFaqPaths,
  getFAQConfig,
  getDynamicsPaths,
  getArticleConfig,
  getArticlePaths,
  getDynamicsConfig,
  getLandingPageConfig,
  getLandingPagePaths,
  getAllArticles,
  getArticleConfigBySite,
  getSiteDomainUrl,
} from "@/lib/api";
import dynamic from "next/dynamic";
import DynamicPage from "../pages-lib/dynamic";
import { generateSitemap } from "../utils/sitemap-helper";
import Error from "next/error";
import Home from "pages";
import appConfig from "../config.json";
import { getPathsWithoutCollision } from "@/lib/slug";

const AgreementsPage = dynamic(() => import("../pages-lib/agreements"));
const AboutUsPage = dynamic(() => import("../pages-lib/aboutUs"));
const FaqsPage = dynamic(() => import("../pages-lib/faq"));
const ArticlePage = dynamic(() => import("../pages-lib/articles"));

const DynamicRoutePage = ({ ...config }) => {
  if (config.config.pageType === "agreements") {
    return <AgreementsPage {...config} />;
  }
  if (config.config.pageType === "abouts") {
    return <AboutUsPage {...config} />;
  }
  if (config.config.pageType === "faqs") {
    return <FaqsPage {...config} />;
  }
  if (config.config.pageType === "dynamics") {
    return <DynamicPage {...config} />;
  }
  if (config.config.pageType === "articles") {
    return <ArticlePage {...config} />;
  }
  if (config.config.pageType === "landingPages") {
    const newConfig = {
      ...config.config[0],
      configuration: config.config.configuration,
    };
    return <Home config={newConfig} />;
  }
  return null;
};

export const getStaticProps = async ({ preview, previewData, params }) => {
  let path = params.pages;

  let agreement = await getAgreementsConfig(!!preview, previewData, path.join("/"));
  if (!!agreement) {
    return await transformStaticProps({
      configuration: { ...(await getGlobalConfig(!!preview, previewData)) },
      ...agreement,
      preview: !!preview,
      previewData: previewData ?? null,
      pageType: "agreements",
    });
  }

  let abouts = await getAboutUsConfig(!!preview, previewData, path.join("/"));
  if (!!abouts) {
    return await transformStaticProps({
      configuration: { ...(await getGlobalConfig(!!preview, previewData)) },
      ...abouts,
      preview: !!preview,
      previewData: previewData ?? null,
      pageType: "abouts",
    });
  }

  let faqs = await getFAQConfig(!!preview, previewData, path.join("/"));
  if (!!faqs) {
    return await transformStaticProps({
      configuration: { ...(await getGlobalConfig(!!preview, previewData)) },
      ...faqs,
      preview: !!preview,
      previewData: previewData ?? null,
      pageType: "faqs",
    });
  }

  let dynamics = await getDynamicsConfig(!!preview, previewData, path.join("/"));
  if (!!dynamics) {
    return await transformStaticProps({
      configuration: { ...(await getGlobalConfig(!!preview, previewData)) },
      ...dynamics,
      preview: !!preview,
      previewData: previewData ?? null,
      pageType: "dynamics",
    });
  }

  let article;
  if (appConfig.site === "Kollarnase") {
    const cleanArticleSlugs = getPathsWithoutCollision(await getAllArticles());
    const siteSlug = cleanArticleSlugs.find((item) => {
      return item.slug === path.join("/");
    });

    const slug = siteSlug.slug.replace(/-\d+$/, "");
    article = await getArticleConfigBySite(!!preview, previewData, slug, siteSlug.site);

    if (!article.CanonicalUrl) {
      const { siteUrl } = await getSiteDomainUrl(siteSlug.site, preview, previewData);
      article.CanonicalUrl = `${siteUrl}/${slug}`;
    }
  } else {
    article = await getArticleConfig(!!preview, previewData, path.join("/"));
  }
  if (!!article) {
    return await transformStaticProps({
      configuration: { ...(await getGlobalConfig(!!preview, previewData)) },
      ...article,
      preview: !!preview,
      previewData: previewData ?? null,
      pageType: "articles",
    });
  }

  let landingPages = await getLandingPageConfig(!!preview, previewData, path.join("/"));
  if (!!landingPages) {
    return await transformStaticProps({
      configuration: { ...(await getGlobalConfig(!!preview, previewData)) },
      ...landingPages,
      preview: !!preview,
      previewData: previewData ?? null,
      pageType: "landingPages",
    });
  }
};

export const getStaticPaths = async ({ preview, previewData }) => {
  const agreementPages = await getAgreementsPaths(!!preview, previewData);
  const agreementsPaths = (agreementPages ?? []).map((page) => {
    if (page.slug === null) {
      throw Error("Slug cannot be null");
    }
    let route = [...page.slug.split("/")];
    return { params: { pages: route } };
  });

  let articlePaths = [];
  if (appConfig.site === "Kollarnase") {
    const cleanArticleSlugs = getPathsWithoutCollision(await getAllArticles());
    cleanArticleSlugs.forEach((item) => {
      let route = [...item.slug.split("/")];
      articlePaths.push({ params: { pages: route } });
    });
  } else {
    const articlePages = await getArticlePaths(!!preview, previewData);
    articlePaths = (articlePages ?? []).map((page) => {
      if (page.slug === null) {
        throw Error("Slug cannot be null");
      }
      let route = [...page.slug.split("/")];
      return { params: { pages: route } };
    });
  }

  const aboutPages = await getAboutUsPaths(!!preview, previewData);
  const aboutusPaths = (aboutPages ?? []).map((page) => {
    if (page.slug === null) {
      throw Error("Slug cannot be null");
    }
    let route = [...page.slug.split("/")];
    return { params: { pages: route } };
  });

  const faqPages = await getFaqPaths(!!preview, previewData);
  const faqPaths = (faqPages ?? []).map((page) => {
    if (page.slug === null) {
      throw Error("Slug cannot be null");
    }
    let route = [...page.slug.split("/")];
    return { params: { pages: route } };
  });

  const landingPages = await getLandingPagePaths(!!preview, previewData);
  const landingPagesPaths = (landingPages ?? []).map((page) => {
    if (page.slug === null) {
      throw Error("Slug cannot be null");
    }
    let route = [...page.slug.split("/")];
    return { params: { pages: route } };
  });

  const dynamicsPages = await getDynamicsPaths(!!preview, previewData);
  const dynamicsPaths = (dynamicsPages ?? []).map((page) => {
    if (page.slug === null) {
      throw Error("Slug cannot be null");
    }
    let route = [...page.slug.split("/")];
    return { params: { pages: route } };
  });

  let allPaths = agreementsPaths
    .concat(aboutusPaths)
    .concat(faqPaths)
    .concat(dynamicsPaths)
    .concat(articlePaths)
    .concat(landingPagesPaths);

  let sitemapSlugs = allPaths.map((entity) => {
    return entity.params.pages.join("/");
  });

  let findDuplicates = sitemapSlugs.filter((item, index) => sitemapSlugs.indexOf(item) !== index);

  if (findDuplicates?.length > 0) {
    console.log(`\n\n ERROR Duplicate slugs detected: ${findDuplicates}`);
    throw `Duplicate slugs detected: ${findDuplicates}`;
  }

  await generateSitemap(sitemapSlugs, articlePaths.length > 0);

  return {
    paths: allPaths,
    fallback: false,
  };
};

export default DynamicRoutePage;
